import axios from 'axios';
import {
    ILoginApiPayload,
    HotelModel,
    DeleteHotelModel,
    DeleteExcursionModel,
    UpdateHotelDetailsModel,
    PlacePutBody,
    GuidePutBody,
    GuideDeleteBody,
    PackagePutBody,
    DeletePackageBody,
    AgentPutBody,
    AgentDeleteBody,
    OfficePutBody,
    OfficeDeleteBody,
    AgencyPutBody,
    AgencyDeleteBody,
    AgencyUpdateBody,
    AgencyOnboardBody,
    IUpsertAgencyApiPayload,
    IUpsertAgencyApiResponse,
    IImportAgencyDataApiResponse,
    QuotationListResponseModel,
    IGetQuotationListApiParams,
    QuotationBasicDetailsResponseModel,
    QuotationAgencyAgentListResponseModel,
    QuotationBasicDetailsPutBody,
    QuotationDetailsResponseModel,
    QuotationJourneyDetailsResponseModel,
    QuotationJourneyDetailsPutBody,
    QuotationPricingSummaryResponseModel,
    QuotationPricingSummaryPutBody,
    QuotationDuplicateRequestModel,
    QuotationDuplicateResponseModel,
    QuotationCloneVersionRequestModel,
    QuotationCloneVersionResponseModel,
    QuotationDeleteResponseModel,
    QuotationHotelOptionsByDateResponseModel,
    QuotationJourneyOptionsByDateResponseModel,
    QuotationJourneyOptionsResponseModel, IChangeQuotationStatusApiPayload, IChangeQuotationStatusApiResponse,
    QuotationFollowUpPutBody,
    QuotationFollowUpSaveResponseModel,
    QuotationFollowUpDeleteResponseModel,
    IGetUserListApiResponse,
    ICreateUserApiPayload,
    ICreateUserApiResponse,
    IUpdateUserApiPayload,
    IUpdateUserApiResponse,
    IGetUserPasswordApiResponse,
    IUpdateUserPasswordApiPayload,
    IUpdateUserPasswordApiResponse,
    IGetCityListApiResponse,
    IUpsertCityApiPayload,
    IUpsertCityApiResponse,
} from '../models/axiosModel';
import { HotelDetailModel, UpdateHotelRoomTypePayload } from '../models';

axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;

// Loader state management
let loaderCallbacks: {
    showLoader: (message?: string) => void;
    hideLoader: () => void;
} | null = null;

export const setLoaderCallbacks = (callbacks: {
    showLoader: (message?: string) => void;
    hideLoader: () => void;
}) => {
    loaderCallbacks = callbacks;
};

// Request interceptor
axios.interceptors.request.use(
    (config) => {
        // Show loader for all requests unless skipLoader is set to true
        if (loaderCallbacks && !(config as any).skipLoader) {
            loaderCallbacks.showLoader('Loading...');
        }
        return config;
    },
    (error) => {
        if (loaderCallbacks && !(error.config as any)?.skipLoader) {
            loaderCallbacks.hideLoader();
        }
        return Promise.reject(error);
    }
);

// Response interceptor
axios.interceptors.response.use(
    (response) => {
        // Hide loader on successful response unless skipLoader is set to true
        if (loaderCallbacks && !(response.config as any).skipLoader) {
            loaderCallbacks.hideLoader();
        }
        return response;
    },
    (error) => {
        // Hide loader on error response unless skipLoader is set to true
        if (loaderCallbacks && !(error.config as any)?.skipLoader) {
            loaderCallbacks.hideLoader();
        }
        return Promise.reject(error);
    }
);

const responseBody = (response: any): any => {
    return response.data;
}

const Error = (err: any): any => {
    return { status: false, error: JSON.stringify(err) };
}

const requests = {
    get: async (url: string, skipLoader?: boolean): Promise<any> => {
        try {
            const controller = new AbortController();
            let result: any = await axios.get(url,
                {
                    headers: {
                        "Content-Type": "application/json",
                        // "Authorization": `Bearer ta_td5hEJP5afQEp7AYgu4gCwIA75mRZAu3HKMJHrEBA1bdfnxKpsePD9dtS6MS88s81771841234813`
                    },
                    signal: controller.signal,
                    skipLoader: skipLoader || false
                } as any).then((response) => {
                    return responseBody(response);
                }).catch((err) => {
                    return Error(err);
                });
            return result;
        } catch (err: any) {
            return Error(err);
        }
    },
    fileUploadPost: async (url: string, body: any, skipLoader?: boolean): Promise<any> => {
        try {
            let result: any = await axios.post(url, body, {
                headers: {
                    "Content-Type": "multipart/form-data"
                },
                skipLoader: skipLoader || false
            } as any).then((response) => {
                return responseBody(response);
            }).catch((err) => {
                return Error(err);
            });
            return result;
        } catch (err: any) {
            return Error(err);
        }
    },
    post: async (url: string, body: any, skipLoader?: boolean, onAbort?: () => void): Promise<any> => {
        try {
            const controller = new AbortController();
            let result: any = await axios.post(url, body, {
                headers: {
                    "Content-Type": "application/json",
                    // "Authorization": `Bearer ta_td5hEJP5afQEp7AYgu4gCwIA75mRZAu3HKMJHrEBA1bdfnxKpsePD9dtS6MS88s81771841234813`
                },
                signal: controller.signal,
                skipLoader: skipLoader || false
            } as any).then((response) => {
                return responseBody(response);
            }).catch((err) => {
                return Error(err);
            });
            return result;
        } catch (err: any) {
            if (axios.isCancel(err)) {
                console.log("Request was canceled", err);
                if (onAbort) console.log("onabort called");
            } else {
                console.log("not aborted", err)
            }
            return Error(err);
        }
    },
    put: async (url: string, body: any, skipLoader?: boolean): Promise<any> => {
        try {
            const response = await axios.put(url, body, {
                headers: {
                    "Content-Type": "application/json",
                    // "Authorization": `Bearer ta_td5hEJP5afQEp7AYgu4gCwIA75mRZAu3HKMJHrEBA1bdfnxKpsePD9dtS6MS88s81771841234813`
                },
                skipLoader: skipLoader || false
            } as any);
            return responseBody(response);
        } catch (err: any) {
            return Error(err);
        }
    },
    delete: async (url: string, skipLoader?: boolean): Promise<any> => {
        try {
            const response = await axios.delete(url, {
                headers: {
                    "Content-Type": "application/json",
                    // "Authorization": `Bearer ta_td5hEJP5afQEp7AYgu4gCwIA75mRZAu3HKMJHrEBA1bdfnxKpsePD9dtS6MS88s81771841234813`
                },
                skipLoader: skipLoader || false
            } as any);
            return responseBody(response);
        } catch (err: any) {
            return Error(err);
        }
    },
    getFile: async (url: string, skipLoader?: boolean): Promise<any> => {
        try {
            const controller = new AbortController();
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                },
                responseType: "blob",
                signal: controller.signal,
                skipLoader: skipLoader || false,
            } as any);
            return response;
        } catch (err: any) {
            return Error(err);
        }
    },
};

const UserMeAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/user/get-user-profile", skipLoader),
    getURL: "/api/user/get-user-profile"
}

const LoginAPI = {
    post: (body: ILoginApiPayload, skipLoader?: boolean): Promise<any> => requests.post("/api/user/login", body, skipLoader),
    postURL: "/api/user/login"
}

const LogoutAPI = {
    post: (): Promise<any> => requests.post("/api/user/logout", {}),
    postURL: "/api/user/logout"
}

const HotelListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/hotel/hotel-list", skipLoader),
    getURL: "/api/hotel/hotel-list"
}

const GenericGlobalAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/global/generic-data", skipLoader),
    getURL: "/api/global/generic-data"
}

const CreateHotelAPI = {
    post: (body: HotelModel, skipLoader?: boolean): Promise<any> => requests.post("/api/hotel/create-hotel", body, skipLoader),
    postURL: "/api/hotel/create-hotel"
}

const HotelDetailAPI = {
    get: (hotel_id: string, skipLoader?: boolean): Promise<any> => requests.get(`/api/hotel/hotel-details?hotel_id=${hotel_id}`, skipLoader),
    getURL: "/api/hotel/hotel-details"
}

const UpdateHotelAPI = {
    put: (body: HotelDetailModel, skipLoader?: boolean): Promise<any> => requests.put("/api/hotel/update-hotel", body, skipLoader),
    putURL: "/api/hotel/update-hotel"
}

const UpdateHotelRoomTypeAPI = {
    put: (body: UpdateHotelRoomTypePayload, skipLoader?: boolean): Promise<any> => requests.put("/api/hotel/hotel-room-type", body, skipLoader),
    putURL: "/api/hotel/hotel-room-type"
}

const UpdateHotelRoomPricingAPI = {
    put: (body: any, skipLoader?: boolean): Promise<any> => requests.put("/api/hotel/hotel-room-pricing", body, skipLoader),
    putURL: "/api/hotel/hotel-room-pricing"
}

const UpdateHotelDetailsAPI = {
    put: (body: UpdateHotelDetailsModel, skipLoader?: boolean): Promise<any> => requests.put("/api/hotel/update-hotel-details", body, skipLoader !== undefined ? skipLoader : true),
    putURL: "/api/hotel/update-hotel-details"
}

const DeleteHotelAPI = {
    put: (body: DeleteHotelModel, skipLoader?: boolean): Promise<any> => requests.put("/api/hotel/delete-hotel", body, skipLoader),
    putURL: "/api/hotel/delete-hotel"
}

const DeleteContractAPI = {
    delete: (contract_id: string, skipLoader?: boolean): Promise<any> =>
        requests.delete(`/api/hotel/delete-contract?contract_id=${encodeURIComponent(contract_id)}`, skipLoader),
    deleteURL: "/api/hotel/delete-contract"
}

const ExcursionsListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/excursion", skipLoader),
    getURL: "/api/excursion"
}

const ExcursionDetailAPI = {
    get: (excursion_id: string, skipLoader?: boolean): Promise<any> =>
        requests.get(`/api/excursion/details?excursion_id=${excursion_id}`, skipLoader),
    getURL: "/api/excursion/details"
}

const UpdateExcursionAPI = {
    put: (body: any, skipLoader?: boolean): Promise<any> => requests.put("/api/excursion", body, skipLoader),
    putURL: "/api/excursion"
}

const DeleteExcursionAPI = {
    put: (body: DeleteExcursionModel, skipLoader?: boolean): Promise<any> => requests.put("/api/excursion/delete-excursion", body, skipLoader),
    putURL: "/api/excursion/delete-excursion"
}

const CheckExcursionUsageAPI = {
    post: (body: { excursion_id: string }, skipLoader?: boolean): Promise<any> =>
        requests.post("/api/excursion/check-usage", body, skipLoader ?? true),
    postURL: "/api/excursion/check-usage",
}

const PlaceListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/transportation/place-list", skipLoader),
    getURL: "/api/transportation/place-list",
    put: (body: PlacePutBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/transportation/place", body, skipLoader),
}

const DeletePlaceAPI = {
    put: (
        body: { place_id: string; flag_active: boolean; place_flag_deleted: boolean },
        skipLoader?: boolean
    ): Promise<any> => requests.put("/api/transportation/delete-place", body, skipLoader),
    putURL: "/api/transportation/delete-place",
}

const CheckPlaceAPI = {
    post: (body: { place_name: string; city_id: number,place_id: string | null }, skipLoader?: boolean): Promise<any> =>
        requests.post("/api/transportation/check-place", body, skipLoader ?? true),
    postURL: "/api/transportation/check-place",
}

const CheckPlaceUsageAPI = {
    post: (body: { place_id: string }, skipLoader?: boolean): Promise<any> =>
        requests.post("/api/transportation/check-place-usage", body, skipLoader ?? true),
    postURL: "/api/transportation/check-place-usage",
}

const GuideListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/guide", skipLoader),
    getURL: "/api/guide",
}

const GuidePutAPI = {
    put: (body: GuidePutBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/guide", body, skipLoader),
    putURL: "/api/guide",
}

const GuideDeleteAPI = {
    put: (body: GuideDeleteBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/guide/delete-guide", body, skipLoader),
    putURL: "/api/guide/delete-guide",
}

const DayTripListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/transportation/day-trip-list", skipLoader),
    getURL: "/api/transportation/day-trip-list",
}

const DayTripDetailAPI = {
    get: (day_trip_id: string, skipLoader?: boolean): Promise<any> =>
        requests.get(`/api/transportation/day-trip-details?day_trip_id=${encodeURIComponent(day_trip_id)}`, skipLoader),
    getURL: "/api/transportation/day-trip-details",
}

const DayTripPutAPI = {
    put: (body: any, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/transportation/day-trip", body, skipLoader),
    putURL: "/api/transportation/day-trip",
}

const DayTripDeleteAPI = {
    put: (body: { day_trip_id: string; flag_active: boolean; day_trip_flag_deleted: boolean }, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/transportation/delete-day-trip", body, skipLoader),
    putURL: "/api/transportation/delete-day-trip",
}

const CheckDayTripUsageAPI = {
    post: (body: { day_trip_id: string }, skipLoader?: boolean): Promise<any> =>
        requests.post("/api/transportation/check-day-trip-usage", body, skipLoader ?? true),
    postURL: "/api/transportation/check-day-trip-usage",
}

const PackageListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/transportation/package-list", skipLoader),
    getURL: "/api/transportation/package-list",
}

const PackageDetailAPI = {
    get: (package_id: string, skipLoader?: boolean): Promise<any> =>
        requests.get(`/api/transportation/package-details?package_id=${encodeURIComponent(package_id)}`, skipLoader),
    getURL: "/api/transportation/package-details",
}

const PackagePutAPI = {
    put: (body: PackagePutBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/transportation/package", body, skipLoader),
    putURL: "/api/transportation/package",
}

const DeletePackageAPI = {
    put: (body: DeletePackageBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/transportation/delete-package", body, skipLoader),
    putURL: "/api/transportation/delete-package",
}

const AgentListAPI = {
    get: (agencyId: string, skipLoader?: boolean): Promise<any> => requests.get(`/api/agency/agent?agency_id=${agencyId}`, skipLoader),
    getURL: "/api/agency/agent",
}

const AgentDetailAPI = {
    get: (agent_id: string, skipLoader?: boolean): Promise<any> =>
        requests.get(`/api/agency/agent-details?agent_id=${encodeURIComponent(agent_id)}`, skipLoader),
    getURL: "/api/agency/agent-details",
}

const AgentPutAPI = {
    put: (body: AgentPutBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/agency/agent", body, skipLoader),
    putURL: "/api/agency/agent",
}

const AgentDeleteAPI = {
    put: (body: AgentDeleteBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/agency/delete-agent", body, skipLoader),
    putURL: "/api/agency/delete-agent",
}

const AgencyListAPI = {
    get: (skipLoader?: boolean): Promise<any> => requests.get("/api/agency", skipLoader),
    getURL: "/api/agency",
}

const AgencyDetailAPI = {
    get: (agency_id: string, skipLoader?: boolean): Promise<any> =>
        requests.get(`/api/agency?agency_id=${agency_id}`, skipLoader),
    getURL: "/api/agency",
}

const AgencyPostAPI = {
    post: (body: AgencyPutBody, skipLoader?: boolean): Promise<any> =>
        requests.post("/api/agency", body, skipLoader),
    putURL: "/api/agency",
}

const AgencyOnboardAPI = {
    post: (body: AgencyOnboardBody, skipLoader?: boolean): Promise<any> =>
        requests.post("/api/agency/onboard", body, skipLoader),
    postURL: "/api/agency/onboard",
}

const AgencyUpsertAPI = {
    post: (body: IUpsertAgencyApiPayload, skipLoader?: boolean): Promise<IUpsertAgencyApiResponse> =>
        requests.post("/api/agency/upsert", body, skipLoader),
    postURL: "/api/agency/upsert",
}

/** Admin only. Multipart field `file` = CSV with headers Agency, Office, Agent, Phone, Email. */
const AgencyImportAPI = {
    post: (file: File, skipLoader?: boolean): Promise<IImportAgencyDataApiResponse> => {
        const body = new FormData()
        body.append("file", file)
        return requests.fileUploadPost("/api/agency/import-agency-data", body, skipLoader)
    },
    postURL: "/api/agency/import-agency-data",
}

const AgencyUpdateAPI = {
    put: (body: AgencyUpdateBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/agency", body, skipLoader),
    putURL: "/api/agency",
}

const AgencyDeleteAPI = {
    put: (body: AgencyDeleteBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/agency/delete-agency", body, skipLoader),
    putURL: "/api/agency/delete-agency",
}

const OfficeListAPI = {
    get: (agency_id: string, skipLoader?: boolean): Promise<any> =>
        requests.get(`/api/agency/office?agency_id=${agency_id}`, skipLoader),
    getURL: "/api/agency/office",
}

const OfficePutAPI = {
    put: (body: OfficePutBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/agency/office", body, skipLoader),
    putURL: "/api/agency/office",
}

const OfficeDeleteAPI = {
    put: (body: OfficeDeleteBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/agency/delete-office", body, skipLoader),
    putURL: "/api/agency/delete-office",
}

const QuotationListAPI = {
    get: (params: IGetQuotationListApiParams, skipLoader?: boolean): Promise<QuotationListResponseModel> => {
        const query = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") query.set(key, String(value))
        })
        return requests.get(`/api/quotation?${query.toString()}`, skipLoader)
    },
    getURL: "/api/quotation",
}

const QuotationBasicDetailsAPI = {
    get: (quotation_id: string, skipLoader?: boolean): Promise<QuotationBasicDetailsResponseModel> =>
        requests.get(`/api/quotation/basic-details?quotation_id=${encodeURIComponent(quotation_id)}`, skipLoader),
    put: (body: QuotationBasicDetailsPutBody, skipLoader?: boolean): Promise<any> =>
        requests.put("/api/quotation/basic-details", body, skipLoader),
    getURL: "/api/quotation/basic-details",
}

const QuotationAgencyAgentListAPI = {
    get: (skipLoader?: boolean): Promise<QuotationAgencyAgentListResponseModel> => requests.get("/api/agency/agency-agent-list", skipLoader),
    getURL: "/api/agency/agency-agent-list",
}

// #5 Delete a quotation (REST style: /api/quotation/{quotation_id})
const QuotationDeleteAPI = {
    delete: (quotation_id: string, skipLoader?: boolean): Promise<QuotationDeleteResponseModel> =>
        requests.delete(`/api/quotation/${encodeURIComponent(quotation_id)}`, skipLoader),
    deleteURL: "/api/quotation",
}

// #6 Get full quotation details (basic + journey + pricing) by id
const QuotationDetailsAPI = {
    get: (quotation_id: string, skipLoader?: boolean): Promise<QuotationDetailsResponseModel> =>
        requests.get(`/api/quotation/${encodeURIComponent(quotation_id)}`, skipLoader),
    getURL: "/api/quotation",
}

const QuotationDocxAPI = {
    get: (quotation_id: string, skipLoader?: boolean): Promise<any> =>
        requests.getFile(`/api/quotation/${encodeURIComponent(quotation_id)}/docx`, skipLoader),
    getURL: "/api/quotation/:quotation_id/docx",
}

const QuotationDuplicateAPI = {
    post: (
        quotation_id: string,
        body: QuotationDuplicateRequestModel,
        skipLoader?: boolean,
    ): Promise<QuotationDuplicateResponseModel> =>
        requests.post(`/api/quotation/${encodeURIComponent(quotation_id)}/duplicate`, body, skipLoader),
    postURL: "/api/quotation/:quotation_id/duplicate",
}

const QuotationCloneVersionAPI = {
    post: (
        quotation_id: string,
        body: QuotationCloneVersionRequestModel,
        skipLoader?: boolean,
    ): Promise<QuotationCloneVersionResponseModel> =>
        requests.post(`/api/quotation/${encodeURIComponent(quotation_id)}/clone-version`, body, skipLoader),
    postURL: "/api/quotation/:quotation_id/clone-version",
}

// #9 GET / #10 PUT Step 2 Journey Details
const QuotationJourneyDetailsAPI = {
    get: (quotation_id: string, skipLoader?: boolean): Promise<QuotationJourneyDetailsResponseModel> =>
        requests.get(`/api/quotation/journey-details?quotation_id=${encodeURIComponent(quotation_id)}`, skipLoader),
    put: (body: QuotationJourneyDetailsPutBody, skipLoader?: boolean): Promise<QuotationJourneyDetailsResponseModel> =>
        requests.put("/api/quotation/journey-details", body, skipLoader),
    getURL: "/api/quotation/journey-details",
}

// #11 GET / #12 PUT Pricing Summary
const QuotationPricingSummaryAPI = {
    get: (quotation_id: string, skipLoader?: boolean): Promise<QuotationPricingSummaryResponseModel> =>
        requests.get(`/api/quotation/pricing-summary?quotation_id=${encodeURIComponent(quotation_id)}`, skipLoader),
    put: (body: QuotationPricingSummaryPutBody, skipLoader?: boolean): Promise<QuotationPricingSummaryResponseModel> =>
        requests.put("/api/quotation/pricing-summary", body, skipLoader),
    getURL: "/api/quotation/pricing-summary",
}

// #13 Change quotation status (Draft → Created → Sent etc.)
const QuotationChangeStatusAPI = {
    put: (body: IChangeQuotationStatusApiPayload, skipLoader?: boolean): Promise<IChangeQuotationStatusApiResponse> =>
        requests.put("/api/quotation/change-status", body, skipLoader),
    putURL: "/api/quotation/change-status",
}

const QuotationFollowUpAPI = {
    put: (
        quotation_id: string,
        body: QuotationFollowUpPutBody,
        skipLoader?: boolean,
    ): Promise<QuotationFollowUpSaveResponseModel> =>
        requests.put(`/api/quotation/${encodeURIComponent(quotation_id)}/follow-ups`, body, skipLoader),
    putURL: "/api/quotation/:quotation_id/follow-ups",
    delete: (
        quotation_follow_up_id: string,
        skipLoader?: boolean,
    ): Promise<QuotationFollowUpDeleteResponseModel> =>
        requests.delete(`/api/quotation/follow-ups/${encodeURIComponent(quotation_follow_up_id)}`, skipLoader),
    deleteURL: "/api/quotation/follow-ups/:quotation_follow_up_id",
}

// #1 Hotels available for given city ids and date
const QuotationHotelOptionsByDateAPI = {
    get: (city_ids: number[] | string, date: string, skipLoader?: boolean): Promise<QuotationHotelOptionsByDateResponseModel> => {
        const cityParam = Array.isArray(city_ids) ? city_ids.join(",") : city_ids
        return requests.get(
            `/api/quotation/hotel-options-by-date?city_ids=${encodeURIComponent(cityParam)}&date=${encodeURIComponent(date)}`,
            skipLoader,
        )
    },
    getURL: "/api/quotation/hotel-options-by-date",
}

// #2 Transfers / Tours / Cruise available for given city ids and date
const QuotationJourneyOptionsByDateAPI = {
    get: (
        city_ids: number[] | string,
        date: string,
        quotation_id: string,
        skipLoader?: boolean,
    ): Promise<QuotationJourneyOptionsByDateResponseModel> => {
        const cityParam = Array.isArray(city_ids) ? city_ids.join(",") : city_ids
        return requests.get(
            `/api/quotation/journey-options-by-date?city_ids=${encodeURIComponent(cityParam)}&date=${encodeURIComponent(date)}&quotation_id=${encodeURIComponent(quotation_id)}`,
            skipLoader,
        )
    },
    getURL: "/api/quotation/journey-options-by-date",
}

// #14 Combined journey options (hotels + tours + transfers + excursions) by quotation id
const QuotationJourneyOptionsAPI = {
    get: (quotation_id: string, skipLoader?: boolean): Promise<QuotationJourneyOptionsResponseModel> =>
        requests.get(`/api/quotation/journey-options?quotation_id=${encodeURIComponent(quotation_id)}`, skipLoader),
    getURL: "/api/quotation/journey-options",
}

const UserListAPI = {
    get: (skipLoader?: boolean): Promise<IGetUserListApiResponse> => requests.get("/api/user/user-list", skipLoader),
    getURL: "/api/user/user-list",
}

const CreateUserAPI = {
    post: (body: ICreateUserApiPayload, skipLoader?: boolean): Promise<ICreateUserApiResponse> =>
        requests.post("/api/user/create-user", body, skipLoader),
    postURL: "/api/user/create-user",
}

const UpdateUserAPI = {
    put: (body: IUpdateUserApiPayload, skipLoader?: boolean): Promise<IUpdateUserApiResponse> =>
        requests.put("/api/user/update-user", body, skipLoader),
    putURL: "/api/user/update-user",
}

const UserPasswordAPI = {
    get: (private_user_id: number, skipLoader?: boolean): Promise<IGetUserPasswordApiResponse> =>
        requests.get(`/api/user/user-password?private_user_id=${private_user_id}`, skipLoader),
    getURL: "/api/user/user-password",
}

const UpdateUserPasswordAPI = {
    put: (body: IUpdateUserPasswordApiPayload, skipLoader?: boolean): Promise<IUpdateUserPasswordApiResponse> =>
        requests.put("/api/user/update-user-password", body, skipLoader),
    putURL: "/api/user/update-user-password",
}

const CityListAPI = {
    get: (skipLoader?: boolean): Promise<IGetCityListApiResponse> => requests.get("/api/global/city-list", skipLoader),
    getURL: "/api/global/city-list",
}

const UpsertCityAPI = {
    put: (body: IUpsertCityApiPayload, skipLoader?: boolean): Promise<IUpsertCityApiResponse> =>
        requests.put("/api/global/city", body, skipLoader),
    putURL: "/api/global/city",
}

export const agent = {
    UserMeAPI,
    LoginAPI,
    LogoutAPI,
    HotelListAPI,
    GenericGlobalAPI,
    CreateHotelAPI,
    HotelDetailAPI,
    UpdateHotelAPI,
    UpdateHotelRoomTypeAPI,
    UpdateHotelRoomPricingAPI,
    UpdateHotelDetailsAPI,
    DeleteHotelAPI,
    DeleteContractAPI,
    ExcursionsListAPI,
    ExcursionDetailAPI,
    UpdateExcursionAPI,
    DeleteExcursionAPI,
    CheckExcursionUsageAPI,
    PlaceListAPI,
    DeletePlaceAPI,
    CheckPlaceAPI,
    CheckPlaceUsageAPI,
    GuideListAPI,
    GuidePutAPI,
    GuideDeleteAPI,
    DayTripListAPI,
    DayTripDetailAPI,
    DayTripPutAPI,
    DayTripDeleteAPI,
    CheckDayTripUsageAPI,
    PackageListAPI,
    PackageDetailAPI,
    PackagePutAPI,
    DeletePackageAPI,
    AgentListAPI,
    AgentDetailAPI,
    AgentPutAPI,
    AgentDeleteAPI,
    AgencyListAPI,
    AgencyDetailAPI,
    AgencyPostAPI,
    AgencyOnboardAPI,
    AgencyUpsertAPI,
    AgencyImportAPI,
    AgencyUpdateAPI,
    AgencyDeleteAPI,
    OfficeListAPI,
    OfficePutAPI,
    OfficeDeleteAPI,
    QuotationListAPI,
    QuotationBasicDetailsAPI,
    QuotationAgencyAgentListAPI,
    QuotationDeleteAPI,
    QuotationDetailsAPI,
    QuotationDocxAPI,
    QuotationDuplicateAPI,
    QuotationCloneVersionAPI,
    QuotationJourneyDetailsAPI,
    QuotationPricingSummaryAPI,
    QuotationChangeStatusAPI,
    QuotationFollowUpAPI,
    QuotationHotelOptionsByDateAPI,
    QuotationJourneyOptionsByDateAPI,
    QuotationJourneyOptionsAPI,
    UserListAPI,
    CreateUserAPI,
    UpdateUserAPI,
    UserPasswordAPI,
    UpdateUserPasswordAPI,
    CityListAPI,
    UpsertCityAPI,
};
